"use client";

import { Check, Ellipsis, Flag, Link as LinkIcon, Share2 } from "lucide-react";
import Link from "@/components/ui/Link";
import { useEffect, useRef, useState } from "react";
import { useUi } from "@/components/ui/use-ui";
import { cn } from "@/lib/cn";

/** "…" button with a small glass menu: share, copy link, report. */
export function MoreMenu({
  path,
  title,
  align = "left",
  size = 38,
  reportHref = "/takedown",
}: {
  path: string;
  title: string;
  align?: "left" | "right";
  size?: number;
  reportHref?: string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const t = useUi();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const url = () => new URL(path, window.location.origin).toString();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard unavailable (insecure context); nothing else to do.
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url: url() });
        setOpen(false);
      } catch {
        // Share sheet dismissed.
      }
    } else {
      await copy();
    }
  };

  const item = "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[14px] text-soft hover:bg-white/[0.08] hover:text-fg";

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label={t.actions.moreOptions}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="grid place-items-center rounded-full text-soft transition-colors hover:bg-white/10 hover:text-fg"
        style={{ width: size, height: size }}
      >
        <Ellipsis size={20} />
      </button>
      {open && (
        <div
          role="menu"
          className={cn(
            "glass-strong absolute top-full z-30 mt-2 w-52 rounded-xl p-1.5 shadow-[0_20px_60px_-15px_rgb(0_0_0/0.9)]",
            align === "right" ? "right-0" : "left-0",
          )}
        >
          <button type="button" role="menuitem" onClick={share} className={item}>
            <Share2 size={17} /> {t.actions.share}
          </button>
          <button type="button" role="menuitem" onClick={copy} className={item}>
            {copied ? <Check size={17} className="text-brand-soft" /> : <LinkIcon size={17} />}
            {copied ? t.actions.linkCopied : t.actions.copyLink}
          </button>
          <Link href={reportHref} role="menuitem" className={item} onClick={() => setOpen(false)}>
            <Flag size={17} /> {t.actions.report}
          </Link>
        </div>
      )}
    </div>
  );
}
